import { FaxTypes, FaxV1 } from '@frontend/api-fax';
import { FaxDraftQueries } from '@frontend/api-fax-draft';
import { useTranslation } from '@frontend/i18n';
import { SchemaFaxService } from '@frontend/schema';
import { useAlert } from '@frontend/design-system';
import { downloadPDF, previewPDF, downloadDeliveryReport, previewReport } from '../utils/download-fax-media';
import { useFaxNavigation } from './use-fax-navigation';

export const useFaxActions = () => {
  const alert = useAlert();
  const { t } = useTranslation('fax');
  const {
    addToListFaxCache,
    getFaxesByIdsFromEndpointCache,
    addFaxesToListArchivedFaxCache,
    addFaxesToListBlockedFaxCache,
    updateFaxInListFaxCache,
    updateFaxInListArchivedFaxCache,
    updateFaxInListBlockedFaxCache,
    removeFromListFaxCache,
    removeFromListArchivedFaxCache,
    removeFromListBlockedFaxCache,
    removeFaxWithContactFromListFaxCache,
    getFaxesWithContactFromListFaxCache,
    getFaxesWithContactFromEndpointCache,
    removeFaxesWithContactFromListBlockedFaxCache,
    updateCountField,
    updateFaxReadStatusInAllCaches,
    adjustFaxCountForArchive,
    getUnreadCountFromListFaxCache,
  } = FaxV1.FaxQueryUpdaters.useListFaxQueryUpdater();
  const { closeThread } = useFaxNavigation();

  const removeDraftFromCache = FaxDraftQueries.useRemoveDraftFromCache();
  const invalidateFaxSearch = FaxV1.FaxQueryUpdaters.useInvalidateFaxSearch();
  const { mutateAsync: mutateDeleteDraft } = FaxDraftQueries.useDeleteDraft({
    onSuccess: () => {
      alert.success(t('Draft Deleted'));
    },
    onError: () => {
      alert.error(t('Error Deleting Draft'));
    },
  });

  const archiveFax = async (faxIds: string[], locationId: string, isArchived: boolean) => {
    const sourceFaxes = isArchived
      ? getFaxesByIdsFromEndpointCache(faxIds, 'ListFaxes')
      : getFaxesByIdsFromEndpointCache(faxIds, 'ListArchivedFaxes');

    await SchemaFaxService.UpdateFaxArchiveStatus({
      faxIds,
      locationId,
      isArchived,
    })
      .then(() => {
        adjustFaxCountForArchive(faxIds, isArchived);
        if (isArchived) {
          const archivedAt = new Date().toISOString();
          alert.success(t('Fax Archived'));
          faxIds.forEach((faxId) => removeFromListFaxCache(faxId));
          addFaxesToListArchivedFaxCache(sourceFaxes.map((fax) => ({ ...fax, archivedAt })));
        } else {
          alert.success(t('Fax Unarchived'));
          faxIds.forEach((faxId) => removeFromListArchivedFaxCache(faxId));
          sourceFaxes.forEach((fax) => addToListFaxCache({ faxData: { ...fax, archivedAt: '' }, locationId }));
        }
        invalidateFaxSearch();
      })
      .catch(() => {
        alert.error(isArchived ? t('Error Archiving Fax') : t('Error Unarchiving Fax'));
      });
  };

  const blockFax = async (locationId: string, faxNumber: string, faxId: string) => {
    const faxesToBlock = getFaxesWithContactFromListFaxCache(faxNumber, locationId);
    const unreadCount = faxesToBlock.filter((fax) => !fax.viewedAt).length;

    await SchemaFaxService.BlockContact({
      locationId,
      faxNumber,
      faxId,
    })
      .then(() => {
        alert.success(t('Contact Blocked'));
        removeFaxWithContactFromListFaxCache(faxNumber, locationId);
        addFaxesToListBlockedFaxCache(
          faxesToBlock.map((fax) => ({
            ...fax,
            faxContact: fax.faxContact ? { ...fax.faxContact, isBlocked: true } : fax.faxContact,
          }))
        );
        updateCountField('faxCount', -unreadCount);
        updateCountField('blockedFaxCount', unreadCount);
      })
      .catch(() => {
        alert.error(t('Error Blocking Contact'));
      });
  };

  const deleteFax = async (
    locationId: string,
    faxIds: string[],
    type: FaxTypes.InboxType,
    locationPhones: string[] = []
  ) => {
    const inboxUnreadCount = type === FaxTypes.InboxType.INBOX ? getUnreadCountFromListFaxCache(faxIds) : 0;

    await SchemaFaxService.DeleteFaxes({
      locationId,
      faxIds,
      locationPhones,
    })
      .then(() => {
        alert.success(t('Fax Deleted'));
        closeThread({ type });
        if (type === FaxTypes.InboxType.INBOX) {
          updateCountField('faxCount', -inboxUnreadCount);
        }
        faxIds.forEach((faxId) => {
          removeFromListFaxCache(faxId);
          if (type === FaxTypes.InboxType.ARCHIVED) {
            removeFromListArchivedFaxCache(faxId);
          } else if (type === FaxTypes.InboxType.BLOCKED) {
            removeFromListBlockedFaxCache(faxId);
          }
        });
        invalidateFaxSearch();
      })
      .catch(() => {
        alert.error(t('Error Deleting Fax'));
      });
  };

  const deleteDraft = async (draftId: string, locationId: string) => {
    await mutateDeleteDraft({ draftId, locationId });
    removeDraftFromCache(draftId);
  };

  const downloadFax = async (faxId: string, filename: string, locationId: string, isFax?: boolean) => {
    const mediaItem = {
      faxId,
      filename,
    };
    try {
      await downloadPDF(mediaItem, locationId, !!isFax);
      alert.success(t('Fax Downloaded'));
    } catch (error) {
      console.error('Error downloading fax:', error);
      alert.error(t('Error Downloading Fax'));
    }
  };

  const downloadFaxDeliveryReport = async (faxId: string, locationId: string): Promise<void> => {
    try {
      await downloadDeliveryReport(faxId, locationId);
      alert.success(t('Delivery Report Downloaded'));
    } catch (error) {
      console.error('Error downloading delivery report:', error);
      alert.error(t('Error Downloading Delivery Report'));
    }
  };

  const downloadFaxAndDeliveryReport = async (
    faxId: string,
    filename: string,
    locationId: string,
    isFax: boolean
  ): Promise<void> => {
    try {
      const mediaItem = {
        faxId,
        filename,
      };
      await downloadPDF(mediaItem, locationId, !!isFax, true);
      alert.success(t('Fax and Report Downloaded'));
    } catch (error) {
      console.error('Error downloading fax and report:', error);
      alert.error(t('Error Downloading Fax and Report'));
    }
  };

  const markReadFax = async (isRead: boolean, locationId: string, faxIds: string[], locationPhones: string[] = []) => {
    const readString = isRead ? 'Unread' : 'Read';
    const markedRead = !isRead;

    await SchemaFaxService.UpdateFaxes({
      hasBeenRead: !isRead,
      locationId,
      faxIds,
      locationPhones,
    })
      .then(() => {
        alert.success(t(`Marked as ${readString}`, { readString }));
        faxIds.forEach((faxId) => updateFaxReadStatusInAllCaches(faxId, markedRead));
        invalidateFaxSearch();
      })
      .catch(() => {
        alert.error(t(`Error marking as ${readString}`, { readString }));
      });
  };

  const previewFax = async (faxId: string, filename: string, locationId: string, isFax?: boolean): Promise<void> => {
    try {
      const mediaItem = {
        faxId,
        filename,
      };
      await previewPDF(mediaItem, locationId, !!isFax);
    } catch (error) {
      console.error('Error previewing fax:', error);
      alert.error(t('Error Previewing Fax'));
    }
  };

  const previewDeliveryReport = async (faxId: string, locationId: string): Promise<void> => {
    try {
      await previewReport(faxId, locationId);
    } catch (error) {
      console.error('Error previewing delivery report:', error);
      alert.error(t('Error Previewing Delivery Report'));
    }
  };

  const previewFaxAndDeliveryReport = async (faxId: string, filename: string, locationId: string): Promise<void> => {
    try {
      const mediaItem = {
        faxId,
        filename,
      };
      await previewPDF(mediaItem, locationId, true, true);
    } catch (error) {
      console.error('Error previewing fax and report:', error);
      alert.error(t('Error Previewing Fax and Report'));
    }
  };

  const tagFax = async (locationId: string, faxIds: string[], tagIds: string[]) => {
    await SchemaFaxService.TagFaxes({
      locationId,
      faxIds,
      tagIds,
    })
      .then(() => {
        alert.success(t(`Fax Tagged`));
        invalidateFaxSearch();
        faxIds.forEach((faxId) => {
          updateFaxInListFaxCache({ faxId, locationId });
          updateFaxInListArchivedFaxCache({ faxId, locationId });
          updateFaxInListBlockedFaxCache({ faxId, locationId });
        });
      })
      .catch(() => {
        alert.error(t('Error Tagging Fax'));
      });
  };

  const retryFax = async (faxId: string, locationId: string) => {
    await SchemaFaxService.Retry({ faxId, locationId })
      .then(({ error }) => {
        if (!!error) throw error;
        alert.success(t('Sending Fax'));
        updateFaxInListFaxCache({ faxId, locationId });
      })
      .catch(() => {
        alert.error(t('Error Sending Fax'));
      });
  };

  const unblockFax = async (locationId: string, faxNumber: string, faxId: string) => {
    const faxesToUnblock = getFaxesWithContactFromEndpointCache(faxNumber, locationId, 'ListBlockedFaxes');
    const unreadCount = faxesToUnblock.filter((fax) => !fax.viewedAt).length;

    await SchemaFaxService.UnblockContact({
      locationId,
      faxNumber,
      faxId,
    })
      .then(() => {
        alert.success(t('Contact Unblocked'));
        removeFaxesWithContactFromListBlockedFaxCache(faxNumber, locationId);
        faxesToUnblock.forEach((fax) =>
          addToListFaxCache({
            faxData: {
              ...fax,
              faxContact: fax.faxContact ? { ...fax.faxContact, isBlocked: false } : fax.faxContact,
            },
            locationId,
          })
        );
        updateCountField('blockedFaxCount', -unreadCount);
        updateCountField('faxCount', unreadCount);
      })
      .catch(() => {
        alert.error(t('Error Unblocking Contact'));
      });
  };

  const untagFax = async (locationId: string, faxId: string, tagIds: string[]) => {
    await SchemaFaxService.UntagFaxes({
      locationId,
      faxId,
      tagIds,
    })
      .then(() => {
        alert.success(t(`Fax Untagged`));
        updateFaxInListFaxCache({ faxId, locationId });
        updateFaxInListArchivedFaxCache({ faxId, locationId });
        updateFaxInListBlockedFaxCache({ faxId, locationId });
        invalidateFaxSearch();
      })
      .catch(() => {
        alert.error(t('Error Untagging Fax'));
      });
  };

  return {
    archiveFax,
    blockFax,
    deleteFax,
    deleteDraft,
    downloadFax,
    downloadFaxAndDeliveryReport,
    downloadFaxDeliveryReport,
    markReadFax,
    previewFax,
    previewDeliveryReport,
    previewFaxAndDeliveryReport,
    retryFax,
    tagFax,
    unblockFax,
    untagFax,
  };
};
